import { MS_PER_SECOND } from "@features/session/constants";
import { getPlatform } from "@memorang/helpers/platform";
import type {
	AIResponsePayload,
	AnsweredFormState,
	BaseItemSchema,
	Confidence,
	Hint,
	ItemResponse,
	OriginalItemTypes,
	SubmittedRawInput,
} from "@memorang/types";
import type { TrackAnswerEventRequest } from "../../api/types";
import { getResponseTimesOut } from "../../helpers/response-timing";
import { buildSectionDurationsMetadata } from "../../helpers/section-timing";
import type { SessionEvent } from "../../hooks/types";

export type BuildCurrentAnswerParams = {
	timeTaken: number;
	confidence?: Confidence;
	isCorrect?: boolean;
	score: number;
	selectedChoiceIds: string[];
	answeredFormStates: Record<string, AnsweredFormState>;
	earnedPoints?: number;
	selectedBlankValues?: Record<string, string>;
	selectedOrder?: string[];
	userResponseText?: string;
};

export const buildCurrentAnswer = ({
	timeTaken,
	confidence,
	isCorrect,
	score,
	selectedChoiceIds,
	answeredFormStates,
	earnedPoints,
	selectedBlankValues,
	selectedOrder,
	userResponseText,
}: BuildCurrentAnswerParams) => ({
	timeTaken,
	confidence: confidence ?? 0,
	correct: isCorrect,
	score,
	selectedChoiceIds,
	answeredFormStates,
	...(earnedPoints !== undefined ? { points: earnedPoints } : {}),
	...(selectedBlankValues ? { selectedBlankValues } : {}),
	...(selectedOrder ? { selectedOrder } : {}),
	...(userResponseText ? { userResponseText } : {}),
});

export type BuildTrackAnswerEventRequestParams = {
	currentItem: BaseItemSchema;
	currentItemId: number;
	itemType?: OriginalItemTypes;
	response: ItemResponse;
	sessionId: number;
	collectionId: number;
	projectId: number;
	organizationId: number;
	isSummativeTest?: boolean;
	isCorrect?: boolean;
	selectedChoiceIds: string[];
	selectedChoiceItemId?: string;
	confidence?: Confidence;
	timeTaken: number;
	itemTimeElapsed?: number;
	timeOnItemSeconds: number;
	sectionElapsedSeconds?: Record<string, number>;
	score: number;
	earnedPoints?: number;
	numDistractors: number;
	useAiScoring: boolean;
	aiNormalizedScore: number;
	normalizedPoints?: number;
	displayPoints?: number;
	aiResponse?: AIResponsePayload;
	markedItemsForReview?: number[];
	highlightedItems?: Map<number, string | undefined>;
	hints: Hint[] | null;
	timeElapsed: number;
	sessionTime?: number;
	sessionEvents: SessionEvent[];
	selectedBlankValues?: Record<string, string>;
	selectedOrder?: string[];
	userResponseText?: string;
	rawInput?: SubmittedRawInput;
};

export const buildTrackAnswerEventRequest = ({
	currentItem,
	currentItemId,
	itemType,
	response,
	sessionId,
	collectionId,
	projectId,
	organizationId,
	isSummativeTest,
	isCorrect,
	selectedChoiceIds,
	selectedChoiceItemId,
	confidence,
	timeTaken,
	itemTimeElapsed,
	timeOnItemSeconds,
	sectionElapsedSeconds = {},
	score,
	earnedPoints,
	numDistractors,
	useAiScoring,
	aiNormalizedScore,
	normalizedPoints,
	displayPoints,
	aiResponse,
	markedItemsForReview,
	highlightedItems,
	hints,
	timeElapsed,
	sessionTime,
	sessionEvents,
	selectedBlankValues,
	selectedOrder,
	userResponseText,
	rawInput,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: flat mapping of computed inputs to the request shape
}: BuildTrackAnswerEventRequestParams): TrackAnswerEventRequest => ({
	itemId: currentItemId,
	itemVersionId: currentItem.versionId || 1,
	sessionId,
	collectionId,
	projectId,
	organizationId,
	sessionType: isSummativeTest ? "SUMMATIVE" : "FORMATIVE",
	itemDetails: {
		type: "ANSWER_EVENT",
		itemType: itemType!,
		variant: itemType!,
	},
	response: {
		selectedChoiceIds: selectedChoiceIds ?? [],
		confidence: confidence ?? 0,
		chosenAnswerId: selectedChoiceItemId!,
		correctness: Boolean(isCorrect),
		...(rawInput ? { userSubmittedAnswer: { rawInput } } : {}),
		...(selectedBlankValues ? { selectedBlankValues } : {}),
		...(selectedOrder ? { selectedOrder } : {}),
		...(userResponseText ? { userResponseText } : {}),
	},
	performance: {
		timeTaken,
		timeElapsed: getResponseTimesOut(response, isSummativeTest)
			? itemTimeElapsed
			: timeOnItemSeconds,
		score: useAiScoring ? aiNormalizedScore : score || 0,
		xp: 0,
		points: earnedPoints,
		...(isSummativeTest && normalizedPoints !== undefined
			? { normalizedPoints }
			: {}),
		...(displayPoints !== undefined ? { displayPoints } : {}),
	},
	settings: {
		shuffled: false,
		numDistractors,
	},
	userInteraction: {
		marked: Boolean(
			currentItem.answer?.marked ||
				markedItemsForReview?.includes(currentItem.id!),
		),
		highlight:
			currentItem.answer?.highlight ||
			highlightedItems?.get(currentItem.id!) ||
			"",
		hints: hints || [],
		sessionTime: timeElapsed + (sessionTime || 0),
		events: sessionEvents,
		timeSpentMs: timeTaken * MS_PER_SECOND,
		...(selectedBlankValues ? { selectedBlankValues } : {}),
		...(selectedOrder ? { selectedOrder } : {}),
		...(userResponseText ? { userResponseText } : {}),
	},
	platform: getPlatform(),
	...(useAiScoring && aiResponse ? { aiResponse } : {}),
	metadata: buildSectionDurationsMetadata(sectionElapsedSeconds),
});
